import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Building2, BarChart3, Settings, Shield, LogOut, TrendingUp, Tag, Layout, 
  Video, FileText, Mail, Users, ClipboardList, Lock, PieChart, UserX,
  ChevronDown, ChevronRight, Globe, Database, Megaphone, UserCheck, Clock, Key
} from 'lucide-react';
import Logo from '../components/Logo';

// Import new modular components
import DashboardView from '../components/superadmin/dashboard/DashboardView';
import ClinicsManagement from '../components/superadmin/dashboard/ClinicsManagement';
import PlatformSettings from '../components/superadmin/dashboard/PlatformSettings';
import EnhancedPricingTiers from '../components/superadmin/EnhancedPricingTiers';
import PlatformAnalytics from '../components/superadmin/PlatformAnalytics';
import AdvancedReports from '../components/superadmin/AdvancedReports';
import HomepageCMS from '../components/superadmin/HomepageCMS';
import PageManager from '../components/superadmin/PageManager';
import DemoRequestsManager from '../components/superadmin/DemoRequestsManager';
import ContentManagement from '../components/superadmin/ContentManagement';
import EmailManagement from '../components/superadmin/EmailManagement';
import SuperAdminManagement from '../components/superadmin/SuperAdminManagement';
import EnhancedAuditLogs from '../components/superadmin/EnhancedAuditLogs';
import SecurityManagement from '../components/superadmin/SecurityManagement';
import HelpSystem from '../components/superadmin/HelpSystem';
import OrphanUsersManagement from '../components/superadmin/OrphanUsersManagement';
import ImpersonationManager from '../components/superadmin/ImpersonationManager';
import TrialManagement from '../components/superadmin/TrialManagement';
import UserPasswordManagement from '../components/superadmin/UserPasswordManagement';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar Menu Item Component
const MenuItem = ({ icon: Icon, label, section, activeSection, onClick }) => (
  <button
    onClick={() => onClick(section)}
    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-left text-sm ${
      activeSection === section
        ? 'bg-teal-600 text-white font-medium'
        : 'hover:bg-gray-800 text-gray-300'
    }`}
  >
    <Icon className="h-4 w-4 flex-shrink-0" />
    <span>{label}</span>
  </button>
);

// Sidebar Category Component
const MenuCategory = ({ icon: Icon, label, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="mb-2">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2 text-gray-400 hover:text-white transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </button>
      {isOpen && (
        <div className="ml-2 space-y-1 mt-1">
          {children}
        </div>
      )}
    </div>
  );
};

export default function SuperAdminDashboard({ user, setUser }) {
  const [stats, setStats] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [statsRes, clinicsRes, tiersRes] = await Promise.all([
        axios.get(`${API_URL}/api/super-admin/platform-stats`),
        axios.get(`${API_URL}/api/admin/clinics`),
        axios.get(`${API_URL}/api/subscription-tiers`).catch(() => ({ data: [] }))
      ]);
      
      setStats(statsRes.data);
      setClinics(clinicsRes.data);
      setPricingTiers(tiersRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('clinic_id');
    setUser(null);
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <div className="w-64 bg-gray-900 text-white flex flex-col">
          <div className="p-6 bg-white">
            <Logo />
          </div>
          <div className="flex-1 bg-gray-900"></div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading platform data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-64 bg-gray-900 text-white flex flex-col">
        <div className="p-6 bg-white">
          <Logo />
        </div>
        
        {/* User Profile & Sign Out - At Top for Visibility */}
        <div className="px-4 py-4 bg-gray-800 border-b border-gray-700">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              {user?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'SA'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name || 'Super Admin'}</p>
              <p className="text-xs text-gray-400 truncate">{user?.email || ''}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200 font-medium text-sm"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </button>
        </div>

        <nav className="flex-1 px-2 py-4 overflow-y-auto">
          {/* Dashboard - Always visible */}
          <div className="mb-4">
            <MenuItem 
              icon={BarChart3} 
              label="Dashboard" 
              section="dashboard" 
              activeSection={activeSection} 
              onClick={setActiveSection} 
            />
          </div>

          {/* Business Management */}
          <MenuCategory icon={Building2} label="Business" defaultOpen={true}>
            <MenuItem icon={Building2} label="Clinics" section="clinics" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={Clock} label="Trial Management" section="trials" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={Tag} label="Pricing Tiers" section="pricing" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={Video} label="Demo Requests" section="demos" activeSection={activeSection} onClick={setActiveSection} />
          </MenuCategory>

          {/* Analytics & Reports */}
          <MenuCategory icon={TrendingUp} label="Analytics" defaultOpen={true}>
            <MenuItem icon={PieChart} label="Reports" section="reports" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={TrendingUp} label="Platform Analytics" section="analytics" activeSection={activeSection} onClick={setActiveSection} />
          </MenuCategory>

          {/* Content Management */}
          <MenuCategory icon={Globe} label="Content" defaultOpen={false}>
            <MenuItem icon={Layout} label="Homepage CMS" section="homepage" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={FileText} label="CMS Pages" section="pages" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={FileText} label="Content Pages" section="content" activeSection={activeSection} onClick={setActiveSection} />
          </MenuCategory>

          {/* Communications */}
          <MenuCategory icon={Megaphone} label="Communications" defaultOpen={false}>
            <MenuItem icon={Mail} label="Email Settings" section="email" activeSection={activeSection} onClick={setActiveSection} />
          </MenuCategory>

          {/* User Management */}
          <MenuCategory icon={Users} label="User Management" defaultOpen={false}>
            <MenuItem icon={Users} label="Super Admins" section="admins" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={Key} label="Password Reset" section="password-reset" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={UserCheck} label="Impersonation" section="impersonation" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={UserX} label="Orphan Users" section="orphan-users" activeSection={activeSection} onClick={setActiveSection} />
          </MenuCategory>

          {/* Security & System */}
          <MenuCategory icon={Shield} label="Security & System" defaultOpen={false}>
            <MenuItem icon={Lock} label="Security" section="security" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={ClipboardList} label="Audit Logs" section="audit" activeSection={activeSection} onClick={setActiveSection} />
            <MenuItem icon={Settings} label="Settings" section="settings" activeSection={activeSection} onClick={setActiveSection} />
          </MenuCategory>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {activeSection === 'dashboard' && (
            <DashboardView stats={stats} setActiveSection={setActiveSection} />
          )}

          {activeSection === 'clinics' && (
            <ClinicsManagement 
              clinics={clinics} 
              pricingTiers={pricingTiers}
              onClinicsChange={loadData}
            />
          )}

          {activeSection === 'trials' && (
            <TrialManagement />
          )}

          {activeSection === 'pricing' && (
            <EnhancedPricingTiers />
          )}

          {activeSection === 'reports' && (
            <AdvancedReports />
          )}

          {activeSection === 'analytics' && (
            <PlatformAnalytics />
          )}

          {activeSection === 'homepage' && (
            <HomepageCMS />
          )}

          {activeSection === 'pages' && (
            <PageManager />
          )}

          {activeSection === 'content' && (
            <ContentManagement />
          )}

          {activeSection === 'demos' && (
            <DemoRequestsManager />
          )}

          {activeSection === 'email' && (
            <EmailManagement />
          )}

          {activeSection === 'admins' && (
            <SuperAdminManagement />
          )}

          {activeSection === 'password-reset' && (
            <UserPasswordManagement />
          )}

          {activeSection === 'orphan-users' && (
            <OrphanUsersManagement />
          )}

          {activeSection === 'impersonation' && (
            <ImpersonationManager />
          )}

          {activeSection === 'security' && (
            <SecurityManagement />
          )}

          {activeSection === 'audit' && (
            <EnhancedAuditLogs />
          )}

          {activeSection === 'settings' && (
            <PlatformSettings />
          )}
        </div>
      </div>

      {/* Help System - Floating Button & Panel */}
      <HelpSystem activeSection={activeSection} />
    </div>
  );
}
